import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AnimatedSplash } from './components/AnimatedSplash';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import Welcome from './pages/Welcome';
import UploadChooser from './pages/UploadChooser';
import Changelog from './pages/Changelog';
import IntroPage from './pages/IntroPage';
import SplashPreview from './pages/SplashPreview';
import AuthCallback from './pages/AuthCallback';
import DashboardLayout from './components/DashboardLayout';
import Discover from './pages/Discover';
import ProjectDetail from './pages/ProjectDetail';
import { lazy, Suspense } from 'react';
// Lazy-loaded so @react-pdf/renderer (~500 KB gz) only lands in the
// bundle when a user actually opens the export editor.
const ProjectExport = lazy(() => import('./pages/ProjectExport'));
import Portfolio from './pages/Portfolio';
import OrbiAI from './pages/OrbiAI';
import OrbiHelp from './pages/OrbiHelp';
import Planverse from './pages/Planverse';
import PlanversePreview from './pages/PlanversePreview';
import Workflow from './pages/Workflow';
import Collections from './pages/Collections';
import UploadProject from './pages/UploadProject';
import EditProject from './pages/EditProject';
import Admin from './pages/Admin';
import Pricing from './pages/Pricing';
import Profile from './pages/Profile';
import EditProfile from './pages/EditProfile';
import SecuritySettings from './pages/SecuritySettings';
import PortfolioSetup from './pages/PortfolioSetup';
import PublicPortfolio from './pages/PublicPortfolio';
import ResetPassword from './pages/ResetPassword';
import { LoadingSpinner } from './components/ui/spinner';
import { Capacitor } from '@capacitor/core';
import { usePushNotifications } from './hooks/usePushNotifications';
import { PurchasesProvider } from './contexts/PurchasesContext';

// On native, show the intro page once (first install). Mark it seen in localStorage.
const isNative = Capacitor.isNativePlatform();
const hasSeenIntro = localStorage.getItem('planbase_intro_seen') === 'true';
const shouldShowIntro = isNative && !hasSeenIntro;

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center">
        <LoadingSpinner
          size="xl"
          message="Loading Planbase..."
          subMessage="Please wait while we set things up"
        />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  // Register for push notifications once user is known (native only)
  usePushNotifications(user?.id);

  // Sync subscription status on load — handled by fetchAndUpdatePlan in AuthContext
  // useSubscription(user?.id);

  return (
    <Routes>
      {/* Intro onboarding — always accessible at /intro; native first-launch redirects here */}
      {/* Dev-only splash preview */}
      <Route path="/splash-preview" element={<SplashPreview />} />

      <Route
        path="/intro"
        element={<IntroPage onDone={() => localStorage.setItem('planbase_intro_seen', 'true')} />}
      />

      <Route 
        path="/login" 
        element={loading ? null : user ? <Navigate to="/" replace /> : <LoginPage />} 
      />
      
      <Route 
        path="/signup" 
        element={loading ? null : user ? <Navigate to="/" replace /> : <SignupPage />} 
      />
      
      <Route 
        path="/auth/callback" 
        element={<AuthCallback />} 
      />

      <Route
        path="/auth/reset-password"
        element={<ResetPassword />}
      />

      {/* Post-signup onboarding — sits outside the DashboardLayout
          because it needs the full viewport with no sidebar. */}
      <Route
        path="/welcome"
        element={
          <ProtectedRoute>
            <Welcome />
          </ProtectedRoute>
        }
      />

      {/* Public Portfolio Route - No Auth Required */}
      <Route 
        path="/portfolio/:username" 
        element={<PublicPortfolio />} 
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Discover />} />
        <Route path="discover" element={<Discover />} />
        <Route path="project/:id" element={<ProjectDetail />} />
        <Route
          path="project/:id/export"
          element={
            <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-muted-foreground">Loading editor…</div>}>
              <ProjectExport />
            </Suspense>
          }
        />
        <Route path="submit-project" element={<UploadProject />} />
        <Route path="my-projects" element={<Navigate to="/portfolio" replace />} />
        <Route path="cms-upload" element={<UploadProject />} />
        <Route path="portfolio/new" element={<UploadChooser />} />
        <Route path="changelog" element={<Changelog />} />
        <Route path="edit-project/:id" element={<EditProject />} />
        <Route path="portfolio" element={<Portfolio />} />
        <Route path="admin" element={<Admin />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="orbi" element={<OrbiAI />} />
        <Route path="orbit-ai" element={<Navigate to="/orbi" replace />} />
        <Route path="orbi/workflow" element={<Workflow />} />
        <Route path="orbi/workflow/:draftId" element={<Workflow />} />
        <Route path="help" element={<OrbiHelp />} />
        <Route path="planverse" element={<Planverse />} />
        <Route path="planverse/:id" element={<PlanversePreview />} />
        <Route path="collections" element={<Collections />} />
        <Route path="profile" element={<Profile />} />
        <Route path="profile/edit" element={<EditProfile />} />
        <Route path="profile/security" element={<SecuritySettings />} />
        <Route path="portfolio-setup" element={<PortfolioSetup />} />
      </Route>

      <Route path="*" element={<Navigate to={shouldShowIntro ? '/intro' : '/'} replace />} />
    </Routes>
  );
}

// Scroll to top on every route change
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PurchasesProvider>
          <AnimatedSplash />
          <ScrollToTop />
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </PurchasesProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
